import React from 'react';
import { Palette, Youtube, TrendingUp, Workflow, MessageSquare, Globe } from 'lucide-react';

const skills = [
  {
    id: 1,
    tag: 'Visual Excellence',
    title: '01. Graphics',
    description: 'Creating stunning visual designs, digital art, and brand identities that capture attention and communicate effectively across all mediums.',
    icon: Palette,
    tools: [
      'Adobe Creative Suite',
      'Figma & Design Tools',
      'Brand Identity Design',
      'Digital Illustration',
      'Visual Storytelling'
    ]
  },
  {
    id: 2,
    tag: 'Content Scaling',
    title: '02. YouTube Automation',
    description: 'Building automated content pipelines and systems that streamline video production, publishing, and channel management for maximum efficiency.',
    icon: Youtube,
    tools: [
      'Video Production Workflows',
      'Content Scheduling Systems',
      'Analytics & Optimization',
      'Script Automation',
      'Thumbnail Generation'
    ]
  },
  {
    id: 3,
    tag: 'Smart Finance',
    title: '03. AI-Driven Finance Systems',
    description: 'Developing intelligent financial applications that leverage AI for forecasting, analysis, and automated decision-making processes.',
    icon: TrendingUp,
    tools: [
      'Predictive Analytics',
      'Financial Modeling',
      'Market Analysis Tools',
      'Risk Assessment Systems',
      'Portfolio Optimization'
    ]
  },
  {
    id: 4,
    tag: 'Digital Products',
    title: '04. SaaS Web & App Development',
    description: 'Building scalable SaaS platforms, product-focused web experiences, and modern applications that drive business growth and user engagement.',
    icon: Globe,
    tools: [
      'Full-Stack Development',
      'SaaS Architecture & Design',
      'Product Web Experiences',
      'Mobile-First Applications',
      'Cloud Infrastructure & APIs'
    ]
  },
  {
    id: 6,
    tag: 'Process Optimization',
    title: '05. Automation Workflows',
    description: 'Designing and implementing automated workflows that eliminate repetitive tasks and streamline business processes for efficiency.',
    icon: Workflow,
    tools: [
      'Process Automation',
      'Zapier & Integration Tools',
      'Custom Scripts & Bots',
      'Data Pipeline Creation',
      'Workflow Orchestration'
    ]
  },
  {
    id: 7,
    tag: 'Conversational AI',
    title: '06. Chatbot Development',
    description: 'Creating intelligent conversational interfaces that provide seamless user experiences and automate customer interactions effectively.',
    icon: MessageSquare,
    tools: [
      'NLP & Intent Recognition',
      'Multi-Platform Integration',
      'Custom Bot Frameworks',
      'Conversational Design',
      'AI Training & Optimization'
    ]
  }
];

const SkillsSection = () => {
  return (
    <section id="skills" className="py-20 px-4 bg-gray-50">
      <div className="max-w-[1400px] mx-auto">
        {/* Section Header */}
        <div className="mb-16">
          <h2 className="text-5xl md:text-7xl lg:text-8xl font-bold text-black mb-4">
            What I do <span className="text-orange-500 font-bold">best?</span>
          </h2>
          <p className="text-base text-gray-900 max-w-3xl font-medium leading-relaxed">
            I build digital products and systems—graphics, SaaS platforms, YouTube automation, AI-driven finance solutions, automation workflows, and chatbot applications—that help businesses scale and succeed.
          </p>
        </div>

        {/* Skills Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {skills.map((skill, index) => {
            const Icon = skill.icon;
            return (
              <div
                key={skill.id}
                className="group bg-white rounded-[30px] p-10 shadow-sm hover:shadow-2xl transition-all duration-500 card-3d relative overflow-hidden"
                style={{
                  animationDelay: `${index * 0.1}s`,
                  animation: 'slide-up 0.8s ease-out forwards',
                  opacity: 0
                }}
              >
                {/* Animated Background Gradient on Hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-[30px]"></div>
                
                <div className="relative z-10">
                  {/* Handwritten Tag */}
                  <p className="text-sm text-gray-700 mb-6 transition-all duration-300 group-hover:text-orange-600" style={{ fontFamily: 'Courier New, monospace', fontWeight: 500 }}>
                    {skill.tag}
                  </p>

                  {/* Icon with 3D hover */}
                  <div className="mb-6 transform transition-all duration-500 group-hover:scale-110 group-hover:rotate-3">
                    <Icon className="w-12 h-12 text-orange-500 transition-all duration-500" strokeWidth={1.5} />
                  </div>

                  {/* Title */}
                  <h3 className="text-4xl md:text-5xl font-bold text-black mb-6 transition-all duration-300 group-hover:translate-x-2">{skill.title}</h3>

                  {/* Description */}
                  <p className="text-gray-900 mb-8 leading-relaxed font-medium text-base transition-all duration-300 group-hover:text-black">{skill.description}</p>

                  {/* Tools with stagger animation */}
                  <ul className="space-y-2.5">
                    {skill.tools.map((tool, idx) => (
                      <li 
                        key={idx} 
                        className="text-sm text-gray-800 font-medium transition-all duration-300 group-hover:translate-x-2 group-hover:text-black"
                        style={{
                          transitionDelay: `${idx * 50}ms`
                        }}
                      >
                        • {tool}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Decorative corner element */}
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-500 to-transparent opacity-0 group-hover:opacity-10 transition-opacity duration-500 rounded-bl-[30px]"></div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SkillsSection;